import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { CarCard } from "@/components/site/CarCard";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { InnerPageRequirements } from "@/components/site/InnerPageRequirements";
import { ReviewsHighlight } from "@/components/site/ReviewsHighlight";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cars } from "@/lib/cars";
import { Phone } from "lucide-react";
import { breadcrumbScript } from "@/lib/schema";

const yvrFaqs = [
  {
    q: "Where exactly do we meet at YVR?",
    a: "For international arrivals, we meet at Level 2 International Arrivals curbside: walk straight out of the customs hall through the meeting-point doors and turn right. For domestic arrivals, it's Level 2 Domestic Arrivals curbside, directly outside Door 8. Our driver holds an Avolus sign with your last name and stands at the pillar between the crosswalk and the arrivals doors. If YVR curbside enforcement moves us, we text you a new meeting point before you clear the gate.",
  },
  {
    q: "What happens if my flight is delayed?",
    a: "Send us your flight number at booking. We track it live with FlightAware and adjust delivery automatically: no fee, no rescheduling calls. A two-hour weather delay from LAX or a diverted flight through Seattle both just push our arrival back to match. If a flight is cancelled entirely, we hold the vehicle for the rebooked arrival at no extra cost provided it's within 24 hours.",
  },
  {
    q: "Do you offer after-hours pickup at YVR?",
    a: "Yes. YVR's last major arrival wave lands between 11:30 PM and 1:00 AM. We deliver right through those windows at no premium. A red-eye arrival at 12:40 AM is treated the same as a 10:00 AM landing. For arrivals between 2:00 AM and 5:00 AM we ask you to flag it at booking so the on-call driver is scheduled specifically for you; there is no after-hours surcharge, but confirmation the day before is required.",
  },
  {
    q: "How much luggage fits in each vehicle?",
    a: "The Mercedes S-Class comfortably holds three large checked bags plus three carry-ons, best for two travellers and a lot of luggage, or four travellers with light packing. The BMW X7 M60i and Cadillac Escalade both swallow six large checked bags plus six carry-ons with the third row down, or four checked bags plus six carry-ons with all three rows up. The long-wheelbase Range Rover offers generous luggage space; tell us how many people and bags you're bringing so we can confirm the right fit for its seating layout. The Porsche Macan takes three checked bags plus two carry-ons. If you're arriving with skis, golf bags, or a stroller, mention it at booking.",
  },
  {
    q: "How long does it take to drive from YVR to downtown, Richmond, or Whistler?",
    a: "YVR to downtown Vancouver is 25 to 40 minutes via the Arthur Laing Bridge and Granville Street, depending on time of day. Allow 45 minutes on weekday afternoons. YVR to central Richmond (Aberdeen, Lansdowne) is 10 to 15 minutes across the Sea Island bridge. YVR to Whistler is 2 hours in good weather and up to 3 hours in winter conditions on the Sea-to-Sky; we recommend pairing the airport pickup with our Whistler delivery route if you're heading straight up.",
  },
  {
    q: "How does the return process work before a departing flight?",
    a: "Drop the vehicle at YVR Departures, Level 3 (domestic or international side depending on your flight) and our driver meets you curbside to take the keys. Aim to arrive 15 minutes before your normal check-in cutoff (so 2h15m for international, 1h15m for domestic). If you'd rather not deal with the drop, park at your hotel or a Metro Vancouver address and we'll collect the car after you leave. This option is included at no extra charge inside Vancouver.",
  },
  {
    q: "Can I pick up at YVR and return somewhere else?",
    a: "Yes. YVR-to-anywhere in Metro Vancouver, YVR-to-Whistler, and YVR-to-Fraser-Valley are all standard. Return can happen at your hotel, residence, another Metro Vancouver address, or back at YVR. You tell us where at booking. There is no drop-fee within the Vancouver / YVR / Fraser Valley zone; Whistler returns are quoted case-by-case based on the timing.",
  },
];

const yvrFleet = cars.filter((c) =>
  ["range-rover-p530-lwb-2024", "mercedes-s-class-2022", "cadillac-escalade-2023", "bmw-x7-m60i-2024", "porsche-macan-2023"].includes(c.slug),
);

export const Route = createFileRoute("/yvr-airport-car-rental")({
  component: YvrLanding,
  head: () => ({
    meta: [
      { title: "YVR Airport Car Rental Delivered to Arrivals | Avolus" },
       { name: "description", content: "YVR luxury car rental delivered to arrivals curbside. Flight tracking and after-hours pickup for Range Rover, S-Class, X7, Escalade and Macan." },
      { property: "og:title", content: "YVR Airport Car Rental Delivered to Arrivals | Avolus" },
      { property: "og:description", content: "Skip the rental counter. Keys in hand at Level 2 arrivals curbside, flight-tracked delivery, after-hours pickup." },
      { property: "og:url", content: "https://avoluscars.com/yvr-airport-car-rental" },
       { property: "og:type", content: "website" },
       { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/yvr-airport-car-rental" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Airport Car Rental",
          provider: { "@id": "https://avoluscars.com/#business" },
          areaServed: { "@type": "Airport", name: "Vancouver International Airport", iataCode: "YVR" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: yvrFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "YVR Airport Car Rental", url: "https://avoluscars.com/yvr-airport-car-rental" },
      ]),
    ],
  }),
});

function YvrLanding() {
  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>YVR Airport Delivery</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              YVR Airport Rental, Delivered to Arrivals Curbside
            </h1>
            <nav aria-label="On this page" className="md:hidden mt-4 flex flex-wrap gap-x-5 text-sm underline underline-offset-4"><a href="#vehicles" className="inline-flex items-center min-h-11">Vehicles</a><a href="#questions" className="inline-flex items-center min-h-11">Questions</a><a href="/rates" className="inline-flex items-center min-h-11">Rates</a></nav>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Skip the rental-counter queue, skip the shuttle bus, and skip the walk to the
              parkade. Avolus meets you at Vancouver International Airport arrivals with the
              car keys in hand, flight-tracked in real time, at no premium for after-hours
              landings.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="/book"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Book Now
              </a>
              <a
                href="tel:+17783798881"
                className="md:hidden inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Phone size={14} /> 778-379-8881
              </a>
              <a
                href="/contact"
                className="hidden md:inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Contact Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>The Meeting Point</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Exactly where we meet at YVR.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                <strong className="text-foreground">International arrivals:</strong> exit the
                customs hall, walk straight through the meeting-point doors, and turn right.
                Our driver waits at the pillar between the crosswalk and the arrivals doors on
                Level 2, holding an Avolus sign with your last name.
              </p>
              <p>
                <strong className="text-foreground">Domestic arrivals:</strong> Level 2 curbside,
                directly outside Door 8. Walk out of the gate area, follow signs to arrivals,
                and step out the automatic doors. The driver is on the sidewalk, not in the
                traffic lane.
              </p>
              <p>
                YVR curbside enforcement occasionally rotates pickup zones for construction or
                short-term restrictions. When that happens we text you an updated meeting point
                and photo of the driver before you clear the gate. No wandering the terminal
                looking for us.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Flight Tracking &amp; Delays</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              We follow the flight, you follow the plan.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Send us your flight number at booking. From that moment we track it live;
                gate changes, ground delays, diversions, weather holds. When the flight lands
                90 minutes late, the vehicle arrives 90 minutes later. No calls, no
                rescheduling fees, no penalty. If a connecting flight is missed and the airline
                rebooks you onto the next day, we hold the vehicle at no additional cost
                provided the new arrival is within a 24-hour window of the original.
              </p>
              <p>
                For arrivals between 11:30 PM and 1:00 AM (the peak international arrival wave
                at YVR) we treat it as a standard delivery, no after-hours surcharge. For
                arrivals in the 2:00-5:00 AM window we ask you to note it at booking so the
                on-call driver is specifically scheduled for you.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Luggage Capacity</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              What fits, by vehicle.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                <strong className="text-foreground"><Link to="/fleet/$slug" params={{ slug: "mercedes-s-class-2022" }} className="underline underline-offset-4 hover:text-foreground">Mercedes-Benz S-Class YVR pickup</Link>:</strong> 3 large
                checked bags + 3 carry-ons. Best for two travellers with real luggage, or four
                travellers packing light. Rear-seat comfort is unmatched for the drive
                downtown.
              </p>
              <p>
                <strong className="text-foreground"><Link to="/fleet/$slug" params={{ slug: "bmw-x7-m60i-2024" }} className="underline underline-offset-4 hover:text-foreground">BMW X7 M60i YVR pickup</Link>:</strong> 6 checked bags + 6
                carry-ons with the third row folded, or 4 checked + 6 carry-ons with all three
                rows up. Handles a family of six with luggage or a ski week for four.
              </p>
              <p>
                <strong className="text-foreground"><Link to="/fleet/$slug" params={{ slug: "cadillac-escalade-2023" }} className="underline underline-offset-4 hover:text-foreground">Cadillac Escalade YVR pickup</Link>:</strong> Same footprint
                as the X7: 6 large checked bags with the third row down, and a taller cargo
                area for garment bags, ski bags, and hard cases. Best pick for wedding parties
                and touring groups.
              </p>
              <p>
                 <strong className="text-foreground"><Link to="/fleet/$slug" params={{ slug: "range-rover-p530-lwb-2024" }} className="underline underline-offset-4 hover:text-foreground">Range Rover P530 LWB YVR pickup</Link>:</strong> Long-wheelbase comfort for the drive from the airport, with room for luggage. Tell us your passenger count and bags when booking so we can confirm the right fit for this vehicle.
               </p>
               <p>
                <strong className="text-foreground"><Link to="/fleet/$slug" params={{ slug: "porsche-macan-2023" }} className="underline underline-offset-4 hover:text-foreground">Porsche Macan YVR pickup</Link>:</strong> 3 checked bags + 2
                carry-ons. Ideal for couples or a family of three. Not the pick if the whole
                party is arriving on the same flight with hockey bags.
              </p>
              <p className="text-sm text-muted-foreground">
                Heading beyond the city after landing? See{" "}
                <Link to="/delivery" className="underline underline-offset-4 hover:text-foreground">our full delivery areas</Link>
                : we hand off at YVR and can pre-position vehicles at hotels in downtown,
                Whistler, and the Fraser Valley.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Drive Times From YVR</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              How long the first drive takes.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                <strong className="text-foreground">YVR to downtown Vancouver:</strong> 25 to 40
                minutes via the Arthur Laing Bridge, Granville Street, and the Granville
                Bridge. Weekday afternoons and Friday evenings can stretch to 45 minutes.
              </p>
              <p>
                <strong className="text-foreground">YVR to central Richmond:</strong> 10 to 15
                minutes across the Sea Island bridge to Aberdeen or Lansdowne. Often the
                shortest drop of the day.
              </p>
              <p>
                <strong className="text-foreground">YVR to Burnaby, North Vancouver, and West Vancouver:</strong>
                {" "}30 to 50 minutes depending on the crossing (Second Narrows or Lions Gate)
                and time of day. The Lions Gate can crawl in summer weekends.
              </p>
              <p>
                <strong className="text-foreground">YVR to Whistler:</strong> 2 hours in fair
                conditions on the Sea-to-Sky, up to 3 hours in winter with snowfall and slower
                convoy traffic around Britannia Beach. Pair the airport pickup with our
                Whistler delivery service if you're heading directly to the resort. We handle
                the driving.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Departure Return</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Returning the car before your flight home.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Drop the vehicle at YVR Departures, Level 3 (the domestic or international
                side depending on your flight) and our driver meets you curbside to take the
                keys. Aim to arrive 15 minutes before your normal check-in cutoff, so 2 hours
                15 minutes before international departures and 1 hour 15 minutes before
                domestic. The handoff itself takes 90 seconds; you walk into the terminal with
                your bags and we drive the car away.
              </p>
              <p>
                If you'd rather not deal with the airport drop at all, leave the vehicle at
                your hotel, residence, or any Metro Vancouver address after you head to the
                airport and we collect it from there. This option is included at no additional
                charge inside Vancouver and to any hotel with valet.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="vehicles" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Airport-Ready Fleet</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground max-w-2xl mb-12">
              Built for the trip ahead.
            </h2>
          </Reveal>
          <div className="compact-fleet grid gap-8 md:grid-cols-2">
            {yvrFleet.map((c, i) => (
              <CarCard key={c.slug} car={c} index={i} compact />
            ))}
          </div>
        </div>
      </section>

      <section id="questions" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>YVR FAQ</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-10">
              Airport-specific questions.
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {yvrFaqs.map((f, i) => (
                <AccordionItem key={i} value={`y-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-base md:text-lg font-display text-foreground py-6 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal>
            <p className="mt-10 text-sm text-muted-foreground">
              Flight arriving soon? <Link to="/book" className="text-foreground underline underline-offset-4">Book online</Link> or call the concierge for last-minute airport delivery.
            </p>
          </Reveal>
        </div>
      </section>

      <ReviewsHighlight eyebrow="YVR Pickup Reviews" />
      <InnerPageRequirements />
    </Layout>
  );
}
